import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  Divider
} from '@mui/material';
import {
  Event as EventIcon,
  Check as CheckIcon,
  Schedule as ScheduleIcon,
  Warning as OverdueIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { SURVEILLANCE_FREQUENCIES } from '../../types/enums';

const FollowUpScheduler = ({ surveillanceSchedule = [], onScheduleAppointment }) => {
  const getStatusChip = (nextDueDate) => {
    if (!nextDueDate) {
      return <Chip label="Not Scheduled" size="small" color="default" />;
    }

    const due = new Date(nextDueDate);
    const today = new Date();
    const daysUntilDue = Math.ceil((due - today) / (1000 * 60 * 60 * 24));

    if (daysUntilDue < 0) {
      return <Chip icon={<OverdueIcon />} label="Overdue" size="small" color="error" />;
    } else if (daysUntilDue <= 14) {
      return <Chip icon={<ScheduleIcon />} label="Due Soon" size="small" color="warning" />;
    } else {
      return <Chip icon={<CheckIcon />} label="Scheduled" size="small" color="success" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Group by due status
  const overdueItems = surveillanceSchedule.filter(item => {
    if (!item.nextDueDate) return false;
    return new Date(item.nextDueDate) < new Date();
  });

  const upcomingItems = surveillanceSchedule.filter(item => {
    if (!item.nextDueDate) return false;
    const daysUntilDue = Math.ceil((new Date(item.nextDueDate) - new Date()) / (1000 * 60 * 60 * 24));
    return daysUntilDue >= 0 && daysUntilDue <= 30;
  });

  const futureItems = surveillanceSchedule.filter(item => {
    if (!item.nextDueDate) return false;
    const daysUntilDue = Math.ceil((new Date(item.nextDueDate) - new Date()) / (1000 * 60 * 60 * 24));
    return daysUntilDue > 30;
  });

  const unscheduledItems = surveillanceSchedule.filter(item => !item.nextDueDate);

  const handleSchedule = (item) => {
    if (onScheduleAppointment) {
      onScheduleAppointment(item);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Follow-Up Schedule
          </Typography>
        </Box>
        <Button startIcon={<AddIcon />} variant="contained" size="small">
          Add Follow-Up
        </Button>
      </Box>

      {surveillanceSchedule.length === 0 ? (
        <Alert severity="info">
          No surveillance items scheduled. Add items from the Survivorship Care Plan.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {/* Overdue */}
          {overdueItems.length > 0 && (
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ borderColor: 'error.main' }}>
                <CardContent>
                  <Typography variant="subtitle1" color="error" fontWeight="bold" gutterBottom>
                    Overdue ({overdueItems.length})
                  </Typography>
                  <List dense>
                    {overdueItems.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={item.testOrExam}
                          secondary={
                            <>
                              Due: {formatDate(item.nextDueDate)} • {item.frequency}
                              <br />
                              {item.purpose}
                            </>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Button
                            size="small"
                            variant="contained"
                            color="error"
                            onClick={() => handleSchedule(item)}
                          >
                            Schedule Now
                          </Button>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Upcoming */}
          {upcomingItems.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ borderColor: 'warning.main' }}>
                <CardContent>
                  <Typography variant="subtitle1" color="warning.main" fontWeight="bold" gutterBottom>
                    Due Within 30 Days ({upcomingItems.length})
                  </Typography>
                  <List dense>
                    {upcomingItems.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={item.testOrExam}
                          secondary={`Due: ${formatDate(item.nextDueDate)}`}
                        />
                        <ListItemSecondaryAction>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleSchedule(item)}
                          >
                            Schedule
                          </Button>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Future */}
          {futureItems.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Scheduled ({futureItems.length})
                  </Typography>
                  <List dense>
                    {futureItems.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={item.testOrExam}
                          secondary={`Due: ${formatDate(item.nextDueDate)} • ${item.frequency}`}
                        />
                        {getStatusChip(item.nextDueDate)}
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Unscheduled */}
          {unscheduledItems.length > 0 && (
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Needs Scheduling ({unscheduledItems.length})
                  </Typography>
                  <List dense>
                    {unscheduledItems.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={item.testOrExam}
                          secondary={`Frequency: ${item.frequency} • ${item.purpose}`}
                        />
                        <ListItemSecondaryAction>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleSchedule(item)}
                          >
                            Schedule
                          </Button>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}
    </Paper>
  );
};

export default FollowUpScheduler;

